import { Injectable, inject } from '@angular/core';
import { ApplyHalfLossFactor, BackupPowerPurchaseDataService, ErrorResponse } from '@enp/platform-api-client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, forkJoin, iif, map, of, switchMap } from 'rxjs';

import {
  mapBppProviderApiModelToStoreModel,
  mapBppPurchaseFormApiModelToStoreModel,
  mapBppSubmissionApiModelToStoreModel,
  mapBppSummaryApiModelToStoreModel,
  mapCreateBppPurchaseFormMtuStoreModelToApiModel,
  mapPebReportApiModelToStoreModel,
} from '@domain/backup-power-purchase';
import { MarketStoreModelEnum, mapErrorResponseApiModelToStoreModel } from '@domain/common';

import { getIsPollingContext } from '@shared/interceptors';
import { CustomDateService } from '@shared/pipes/custom-date';
import { isDefined } from '@shared/utils/functions';
import { isBefore, toUtcIso } from '@shared/utils/zoned-date';

import { CoreActions } from '@stores/core';

import { BppActions } from './backup-power-purchase.actions';
import { fromBackupPowerPurchase } from './backup-power-purchase.selector';
import {
  BppGetPebReportsStoreModel,
  BppGetPurchaseFormStoreModel,
  BppGetSubmissionsStoreModel,
  BppSubmitPurchaseFormStoreModel,
} from './models';

@Injectable()
export class BackupPowerPurchaseEffects {
  readonly #actions$ = inject(Actions);
  readonly #backupPowerPurchaseDataService = inject(BackupPowerPurchaseDataService);
  readonly #store = inject(Store);
  readonly #translateService = inject(TranslateService);
  readonly #customDateService = inject(CustomDateService);

  public getProviders$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.getBppProviders),
      concatLatestFrom(() => this.#store.select(fromBackupPowerPurchase.selectProviders$)),
      switchMap(([, selectedProviders]) =>
        iif(
          () => isDefined(selectedProviders),
          of(BppActions.bppProvidersCacheFound()),
          this.#backupPowerPurchaseDataService.getBppProviders().pipe(
            map((providers) =>
              BppActions.getBppProvidersSuccess({
                providers: providers.map((provider) => mapBppProviderApiModelToStoreModel(provider)),
              })
            ),
            catchError(({ error }: { error: ErrorResponse }) =>
              of(
                BppActions.getBppProvidersFail({
                  errorMsg: mapErrorResponseApiModelToStoreModel(error),
                })
              )
            )
          )
        )
      )
    );
  });

  public requestFails$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(
        BppActions.getBppProvidersFail,
        BppActions.getBppSubmissionsFail,
        BppActions.getBppPurchaseFormForFrMarketFail,
        BppActions.getBppPurchaseFormForGbMarketFail,
        BppActions.getBppSummaryFail,
        BppActions.getPebReportsFail
      ),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public getSubmissions$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.getBppSubmissions),
      switchMap(({ action }) =>
        iif(
          () => action.markets.length === 0 || action.statuses.length === 0 || action.participantIds.length === 0,
          of(BppActions.getBppSubmissionsIncorrectRequest()),
          this.#backupPowerPurchaseDataService.getBppSubmissions(...this.mapBppGetSubmissionStoreModelToApiModel(action)).pipe(
            map((submissions) =>
              BppActions.getBppSubmissionsSuccess({
                submissions: submissions.map((submission) => mapBppSubmissionApiModelToStoreModel(submission)),
              })
            ),
            catchError(({ error }: { error: ErrorResponse }) =>
              of(
                BppActions.getBppSubmissionsFail({
                  errorMsg: mapErrorResponseApiModelToStoreModel(error),
                })
              )
            )
          )
        )
      )
    );
  });

  public setSuccessToastMessageOnResubmitSubmissionSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.resubmitBppSubmissionSuccess),
      switchMap(() => this.#translateService.get('imbalance-trading.tabs.submissions.list.resubmit.toasts.success')),
      map((message: string) => CoreActions.setSuccessToastMessage({ toast: { message } }))
    );
  });

  public getSubmissionsOnResubmitSubmissionSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.resubmitBppSubmissionSuccess),
      concatLatestFrom(() => this.#store.select(fromBackupPowerPurchase.selectGetSubmissionsAction$)),
      switchMap(([, action]) =>
        iif(
          () => isDefined(action),
          of(
            BppActions.getBppSubmissions({
              action: action as BppGetSubmissionsStoreModel,
            })
          ),
          of(BppActions.noGetBppSubmissionsActionFound())
        )
      )
    );
  });

  public resubmitSubmission$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.resubmitBppSubmission),
      switchMap(({ id, channel }) =>
        this.#backupPowerPurchaseDataService.resubmitBppSubmission(id, channel).pipe(
          map(() => BppActions.resubmitBppSubmissionSuccess()),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(
              BppActions.resubmitBppSubmissionFail({
                errorCode: error.code,
                errorMsg: error.message ?? '',
              })
            )
          )
        )
      )
    );
  });

  public resubmitSubmissionBatch$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.resubmitBppSubmissionBatch),
      switchMap(({ submissions }) =>
        forkJoin(
          submissions.map((submission) =>
            this.#backupPowerPurchaseDataService.resubmitBppSubmission(submission.id, submission.channel).pipe(
              map(() => true),
              catchError(() => of(false))
            )
          )
        ).pipe(
          map((responses) => {
            return responses.reduce(
              (acc, curr) => (curr ? { ...acc, success: acc.success + 1 } : { ...acc, fail: acc.fail + 1 }),

              { success: 0, fail: 0 }
            );
          }),
          map(({ success, fail }) => BppActions.resubmitBppSubmissionBatchResponse({ success, fail }))
        )
      )
    );
  });

  public setSuccessToastMessageOnResubmitSubmissionBatchResponse$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.resubmitBppSubmissionBatchResponse),
      concatLatestFrom(({ success, fail }) =>
        this.#translateService.get('imbalance-trading.tabs.submissions.list.resubmit.batch-response', {
          successCount: success,
          failCount: fail,
        })
      ),
      switchMap(([{ fail }, translation]) =>
        iif(
          () => fail > 0,
          of(
            CoreActions.setErrorToastMessage({
              toast: { message: translation },
            })
          ),
          of(
            CoreActions.setSuccessToastMessage({
              toast: { message: translation },
            })
          )
        )
      )
    );
  });

  public setSubmissionStatus$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.setBppSubmissionStatus),
      switchMap(({ id, status }) =>
        this.#backupPowerPurchaseDataService.setBppSubmissionStatus(id, { status }).pipe(
          map(() => BppActions.setBppSubmissionStatusSuccess()),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(
              BppActions.setBppSubmissionStatusFail({
                errorCode: error.code,
                errorMsg: error.message ?? '',
              })
            )
          )
        )
      )
    );
  });

  public setPebReportStatus$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.setPebReportStatus),
      switchMap(({ id, status }) =>
        this.#backupPowerPurchaseDataService.setPebReportStatus(id, { status }).pipe(
          map(() => BppActions.setPebReportStatusSuccess()),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(
              BppActions.setPebReportStatusFail({
                errorCode: error.code,
                errorMsg: error.message ?? '',
              })
            )
          )
        )
      )
    );
  });

  public getPebReportsOnSetPebReportStatusSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.setPebReportStatusSuccess),
      concatLatestFrom(() => this.#store.select(fromBackupPowerPurchase.selectGetPebReportsAction$)),
      switchMap(([, pebReportsAction]) =>
        iif(
          () => isDefined(pebReportsAction),
          of(BppActions.getPebReports({ action: pebReportsAction as BppGetPebReportsStoreModel })),
          of(BppActions.noGetBppSubmissionsActionFound())
        )
      )
    );
  });

  public setPebReportStatusFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.setPebReportStatusFail),
      switchMap(({ errorCode, errorMsg }) =>
        this.#translateService.get('imbalance-trading.tabs.submissions.toasts.set-peb-status-error', {
          code: errorCode,
          message: errorMsg,
        })
      ),
      map((errorMsg) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public resubmitSubmissionFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.resubmitBppSubmissionFail),
      switchMap(({ errorCode, errorMsg }) =>
        this.#translateService.get('imbalance-trading.tabs.submissions.list.resubmit.toasts.toasts.error', {
          code: errorCode,
          message: errorMsg,
        })
      ),
      map((errorMsg) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public getBppPurchaseFormForFRMarket$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.getBppPurchaseFormForFrMarket),
      switchMap(({ requestData, isPolling }) =>
        this.#backupPowerPurchaseDataService
          .getBppPurchaseForm(...this.mapBppGetPurchaseFormStoreModelToApiModel(requestData), undefined, undefined, {
            context: getIsPollingContext(isPolling),
          })
          .pipe(
            map((purchaseForm) =>
              BppActions.getBppPurchaseFormForFrMarketSuccess({
                purchaseForm: mapBppPurchaseFormApiModelToStoreModel(purchaseForm),
                connectToCrisis: requestData.connectedToCrisis,
              })
            ),
            catchError(({ error }: { error: ErrorResponse }) =>
              of(
                BppActions.getBppPurchaseFormForFrMarketFail({
                  errorMsg: mapErrorResponseApiModelToStoreModel(error),
                })
              )
            )
          )
      )
    );
  });

  public getBppPurchaseFormForGBMarket$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.getBppPurchaseFormForGbMarket),
      switchMap(({ requestData, isPolling }) =>
        this.#backupPowerPurchaseDataService
          .getBppPurchaseForm(...this.mapBppGetPurchaseFormStoreModelToApiModel(requestData), undefined, undefined, {
            context: getIsPollingContext(isPolling),
          })
          .pipe(
            map((purchaseForm) =>
              BppActions.getBppPurchaseFormForGbMarketSuccess({
                purchaseForm: mapBppPurchaseFormApiModelToStoreModel(purchaseForm),
                connectToCrisis: requestData.connectedToCrisis,
              })
            ),
            catchError(({ error }: { error: ErrorResponse }) =>
              of(
                BppActions.getBppPurchaseFormForGbMarketFail({
                  errorMsg: mapErrorResponseApiModelToStoreModel(error),
                })
              )
            )
          )
      )
    );
  });

  public refreshPurchaseForm$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.refreshBppPurchaseForm),
      concatLatestFrom(() => [
        this.#store.select(fromBackupPowerPurchase.selectLastPurchaseFormRequestData$),
        this.#store.select(fromBackupPowerPurchase.selectCrisisLastPurchaseFormRequestData$),
      ]),
      switchMap(([_, previousRequest, crisisLastRequestData]) =>
        iif(
          () => isDefined(previousRequest),
          iif(
            () => previousRequest!.market === MarketStoreModelEnum.FR,
            of(
              BppActions.getBppPurchaseFormForFrMarket({
                requestData: previousRequest!,
                isPolling: false,
              })
            ),
            of(
              BppActions.getBppPurchaseFormForGbMarket({
                requestData: previousRequest!,
                isPolling: false,
              })
            )
          ),
          iif(
            () =>
              isDefined(crisisLastRequestData.find(({ market }) => market === MarketStoreModelEnum.FR)) &&
              isDefined(crisisLastRequestData.find(({ market }) => market === MarketStoreModelEnum.GB)),
            of(
              BppActions.getBppPurchaseFormForFrMarket({
                requestData: crisisLastRequestData.find(({ market }) => market === MarketStoreModelEnum.FR)!,
                isPolling: false,
              }),
              BppActions.getBppPurchaseFormForGbMarket({
                requestData: crisisLastRequestData.find(({ market }) => market === MarketStoreModelEnum.GB)!,
                isPolling: false,
              })
            ),
            of(BppActions.noPreviousBppPurchaseFormRequestFound())
          )
        )
      )
    );
  });

  public submitBppPurchaseFormForFRMarket$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.submitBppPurchaseFormForFrMarket),
      concatLatestFrom(() => [
        this.#store.select(fromBackupPowerPurchase.selectLastPurchaseFormRequestData$),
        this.#store.select(fromBackupPowerPurchase.selectCrisisLastPurchaseFormRequestData$),
      ]),
      switchMap(([{ requestData }, lastRequestData, crisisLastRequestData]) =>
        this.getSubmitBppPurchaseFormResultAction(requestData, crisisLastRequestData, lastRequestData, MarketStoreModelEnum.FR)
      )
    );
  });

  public refreshPurchaseFormOnSubmitBppPurchaseFormForFrMarketSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.submitBppPurchaseFormForFrMarketSuccess),
      concatLatestFrom(() => [
        this.#store.select(fromBackupPowerPurchase.selectLastPurchaseFormRequestData$),
        this.#store.select(fromBackupPowerPurchase.selectCrisisLastPurchaseFormRequestData$),
      ]),
      switchMap(([_, previousRequest, crisisPreviousRequest]) =>
        this.getRefreshBppPurchaseFormAction(previousRequest, crisisPreviousRequest, MarketStoreModelEnum.FR)
      )
    );
  });

  public submitBppPurchaseFormForGBMarket$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.submitBppPurchaseFormForGbMarket),
      concatLatestFrom(() => [
        this.#store.select(fromBackupPowerPurchase.selectLastPurchaseFormRequestData$),
        this.#store.select(fromBackupPowerPurchase.selectCrisisLastPurchaseFormRequestData$),
      ]),
      switchMap(([{ requestData }, lastRequestData, crisisLastRequestData]) =>
        this.getSubmitBppPurchaseFormResultAction(requestData, crisisLastRequestData, lastRequestData, MarketStoreModelEnum.GB)
      )
    );
  });

  public refreshPurchaseFormOnSubmitBppPurchaseFormForGbMarketSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.submitBppPurchaseFormForGbMarketSuccess),
      concatLatestFrom(() => [
        this.#store.select(fromBackupPowerPurchase.selectLastPurchaseFormRequestData$),
        this.#store.select(fromBackupPowerPurchase.selectCrisisLastPurchaseFormRequestData$),
      ]),
      switchMap(([_, previousRequest, crisisPreviousRequest]) =>
        this.getRefreshBppPurchaseFormAction(previousRequest, crisisPreviousRequest, MarketStoreModelEnum.GB)
      )
    );
  });

  public setSuccessToastMessageOnSubmitBppPurchaseFormSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.submitBppPurchaseFormForFrMarketSuccess, BppActions.submitBppPurchaseFormForGbMarketSuccess),
      switchMap(() => this.#translateService.get('imbalance-trading.tabs.purchase-form.toasts.success')),
      map((message: string) => CoreActions.setSuccessToastMessage({ toast: { message } }))
    );
  });

  public submitBppPurchaseFormFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.submitBppPurchaseFormForFrMarketFail, BppActions.submitBppPurchaseFormForGbMarketFail),
      switchMap(({ errorCode, errorMsg }) =>
        this.#translateService.get('imbalance-trading.tabs.purchase-form.toasts.submit-failed-toast', {
          code: errorCode,
          message: errorMsg,
        })
      ),
      map((errorMsg) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public getSummary$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.getBppSummary),
      switchMap(({ submissionIds }) =>
        this.#backupPowerPurchaseDataService.getBppSummary(submissionIds).pipe(
          map((summary) =>
            BppActions.getBppSummarySuccess({
              summary: mapBppSummaryApiModelToStoreModel(summary),
            })
          ),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(
              BppActions.getBppSummaryFail({
                errorMsg: mapErrorResponseApiModelToStoreModel(error),
              })
            )
          )
        )
      )
    );
  });

  public getBppSubmissionsOnSetSubmissionStatusSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.setBppSubmissionStatusSuccess),
      concatLatestFrom(() => this.#store.select(fromBackupPowerPurchase.selectGetSubmissionsAction$)),
      switchMap(([, submissionsAction]) =>
        iif(
          () => isDefined(submissionsAction),
          of(
            BppActions.getBppSubmissions({
              action: submissionsAction as BppGetSubmissionsStoreModel,
            })
          ),
          of(BppActions.noGetBppSubmissionsActionFound())
        )
      )
    );
  });

  public setSubmissionStatusFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.setBppSubmissionStatusFail),
      switchMap(({ errorCode, errorMsg }) =>
        this.#translateService.get('imbalance-trading.tabs.submissions.toasts.set-status-error', {
          code: errorCode,
          message: errorMsg,
        })
      ),
      map((errorMsg) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public getPebReports$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(BppActions.getPebReports),
      switchMap(({ action }) =>
        this.#backupPowerPurchaseDataService.getPebReports(...this.mapBppGetPebReportsStoreModelToApiModel(action)).pipe(
          map((pebReports) =>
            BppActions.getPebReportsSuccess({
              pebReports: pebReports
                .map((pebReport) => mapPebReportApiModelToStoreModel(pebReport))
                .sort((pebReportA, pebReportB) => (isBefore(pebReportA.submittedAtDateTime, pebReportB.submittedAtDateTime) ? 1 : -1)),
            })
          ),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(
              BppActions.getPebReportsFail({
                errorMsg: mapErrorResponseApiModelToStoreModel(error),
              })
            )
          )
        )
      )
    );
  });

  private mapBppGetSubmissionStoreModelToApiModel(request: BppGetSubmissionsStoreModel) {
    return [
      request.participantIds,
      request.statuses,
      request.markets,
      request.connectedToCrisis,
      request.sortBy,
      request.submissionDatePeriodFromDateTime
        ? this.#customDateService.transformToApiModel(request.submissionDatePeriodFromDateTime)
        : undefined,
      request.submissionDatePeriodToDateTime
        ? this.#customDateService.transformToApiModel(request.submissionDatePeriodToDateTime)
        : undefined,
      request.deliveryDayPeriodFromDateTime
        ? this.#customDateService.transformToApiModel(request.deliveryDayPeriodFromDateTime)
        : undefined,
      request.deliveryDayPeriodToDateTime ? this.#customDateService.transformToApiModel(request.deliveryDayPeriodToDateTime) : undefined,
    ] as const;
  }

  private mapBppGetPebReportsStoreModelToApiModel(request: BppGetPebReportsStoreModel) {
    return [
      this.#customDateService.transformToApiModel(request.submissionDatePeriodFromDateTime),
      this.#customDateService.transformToApiModel(request.submissionDatePeriodToDateTime),
    ] as const;
  }

  private mapBppGetPurchaseFormStoreModelToApiModel(requestData: BppGetPurchaseFormStoreModel) {
    return [
      requestData.participantId,
      toUtcIso(requestData.deliveryPeriodStartDateTime)!,
      toUtcIso(requestData.deliveryPeriodEndDateTime)!,
      requestData.market,
      requestData.connectedToCrisis ? ApplyHalfLossFactor.LOSS_ADJUSTED : ApplyHalfLossFactor.NOMINATED,
    ] as const;
  }

  private mapBppSubmitPurchaseFormStoreModelToApiModel(
    lastGetRequestData: BppGetPurchaseFormStoreModel,
    submitRequestData: BppSubmitPurchaseFormStoreModel
  ) {
    return [
      lastGetRequestData.participantId,
      toUtcIso(lastGetRequestData.deliveryPeriodStartDateTime)!,
      toUtcIso(lastGetRequestData.deliveryPeriodEndDateTime)!,
      lastGetRequestData.market,
      submitRequestData.tradeType,
      submitRequestData.connectToCrisis,
      submitRequestData.createBppPurchaseFormMtus.map((purchaseFormMtu) =>
        mapCreateBppPurchaseFormMtuStoreModelToApiModel(purchaseFormMtu)
      ),
    ] as const;
  }

  private getSubmitBppPurchaseFormResultAction(
    requestData: BppSubmitPurchaseFormStoreModel,
    crisisLastRequestData: BppGetPurchaseFormStoreModel[],
    lastRequestData: BppGetPurchaseFormStoreModel | undefined,
    purchaseFormMarket: MarketStoreModelEnum
  ) {
    return requestData.connectToCrisis
      ? iif(
          () => isDefined(crisisLastRequestData.find(({ market }) => market === purchaseFormMarket)),
          this.#backupPowerPurchaseDataService
            .submitBppPurchaseForm(
              ...this.mapBppSubmitPurchaseFormStoreModelToApiModel(
                crisisLastRequestData.find(({ market }) => market === purchaseFormMarket)!,
                requestData
              )
            )
            .pipe(
              map(() => this.getSubmitBppPurchaseFormSuccessAction(purchaseFormMarket, true)),
              catchError(({ error }: { error: ErrorResponse }) => of(this.getSubmitBppPurchaseFormFailAction(purchaseFormMarket, error)))
            ),
          of(BppActions.noPreviousBppPurchaseFormRequestFound())
        )
      : iif(
          () => isDefined(lastRequestData),
          this.#backupPowerPurchaseDataService
            .submitBppPurchaseForm(...this.mapBppSubmitPurchaseFormStoreModelToApiModel(lastRequestData!, requestData))
            .pipe(
              map(() => this.getSubmitBppPurchaseFormSuccessAction(purchaseFormMarket, false)),
              catchError(({ error }: { error: ErrorResponse }) => of(this.getSubmitBppPurchaseFormFailAction(purchaseFormMarket, error)))
            ),
          of(BppActions.noPreviousBppPurchaseFormRequestFound())
        );
  }

  private getSubmitBppPurchaseFormSuccessAction(market: MarketStoreModelEnum, connectedToCrisis: boolean) {
    return market === MarketStoreModelEnum.FR
      ? BppActions.submitBppPurchaseFormForFrMarketSuccess({
          connectedToCrisis,
        })
      : BppActions.submitBppPurchaseFormForGbMarketSuccess({
          connectedToCrisis,
        });
  }

  private getSubmitBppPurchaseFormFailAction(market: MarketStoreModelEnum, error: ErrorResponse) {
    return market === MarketStoreModelEnum.FR
      ? BppActions.submitBppPurchaseFormForFrMarketFail({
          errorCode: error.code,
          errorMsg: error.message ?? '',
        })
      : BppActions.submitBppPurchaseFormForGbMarketFail({
          errorCode: error.code,
          errorMsg: error.message ?? '',
        });
  }

  private getRefreshBppPurchaseFormAction(
    previousRequest: BppGetPurchaseFormStoreModel | undefined,
    crisisPreviousRequest: BppGetPurchaseFormStoreModel[],
    purchaseFormMarket: MarketStoreModelEnum
  ) {
    return iif(
      () => isDefined(previousRequest),
      of(
        purchaseFormMarket === MarketStoreModelEnum.FR
          ? BppActions.getBppPurchaseFormForFrMarket({
              requestData: previousRequest!,
              isPolling: false,
            })
          : BppActions.getBppPurchaseFormForGbMarket({
              requestData: previousRequest!,
              isPolling: false,
            })
      ),
      iif(
        () => isDefined(crisisPreviousRequest.find(({ market }) => market === purchaseFormMarket)),
        of(
          purchaseFormMarket === MarketStoreModelEnum.FR
            ? BppActions.getBppPurchaseFormForFrMarket({
                requestData: crisisPreviousRequest.find(({ market }) => market === MarketStoreModelEnum.FR)!,
                isPolling: false,
              })
            : BppActions.getBppPurchaseFormForGbMarket({
                requestData: crisisPreviousRequest.find(({ market }) => market === MarketStoreModelEnum.GB)!,
                isPolling: false,
              })
        ),
        of(BppActions.noPreviousBppPurchaseFormRequestFound())
      )
    );
  }
}
